import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import {
  Brain, Dumbbell, ClipboardCheck, TrendingUp, Trophy,
  Heart, Beaker, BarChart3, ChevronRight, ChevronLeft, X, Sparkles
} from "lucide-react";

const TOUR_STEPS = [
  {
    icon: Sparkles,
    color: "text-sky-500",
    bg: "bg-sky-100",
    titleKey: "Welcome to LumiFit",
    descKey: "Your AI-powered companion for physical fitness and cognitive wellness. 50% body, 50% mind — perfectly balanced training plans tailored to how you feel today.",
  },
  {
    icon: ClipboardCheck,
    color: "text-indigo-500",
    bg: "bg-indigo-100",
    titleKey: "Daily Questionnaire",
    descKey: "Start each day with a quick wellness check. Your answers shape a personalized training plan combining physical exercises with cognitive and emotional resilience activities.",
  },
  {
    icon: Dumbbell,
    color: "text-emerald-500",
    bg: "bg-emerald-100",
    titleKey: "AI Training Plans",
    descKey: "Get dynamic workout plans generated by AI based on your current mental and physical state. Each plan adapts to your energy, stress, and mood levels.",
  },
  {
    icon: Brain,
    color: "text-purple-500",
    bg: "bg-purple-100",
    titleKey: "Brain Games",
    descKey: "Sharpen your mind with 5 cognitive games — Memory Sequence, Reaction Test, Math Sprint, Classic Memory, and Word Recall. Choose your difficulty: Easy, Medium, or Hard.",
  },
  {
    icon: Heart,
    color: "text-rose-500",
    bg: "bg-rose-100",
    titleKey: "Mindfulness & Meditation",
    descKey: "Guided breathing exercises and meditation sessions to build emotional resilience. Available anytime you need a moment of calm.",
  },
  {
    icon: Beaker,
    color: "text-cyan-500",
    bg: "bg-cyan-100",
    titleKey: "Deep Dive Assessments",
    descKey: "Clinical-grade psychological questionnaires (PHQ-9, GAD-7, and more) with AI interpretation. Track your mental wellness over time with professional-level insights.",
  },
  {
    icon: Trophy,
    color: "text-amber-500",
    bg: "bg-amber-100",
    titleKey: "Gamification & Streaks",
    descKey: "Earn achievements, build daily streaks, complete weekly challenges, and climb the leaderboard. Stay motivated with rewards for consistency.",
  },
  {
    icon: BarChart3,
    color: "text-teal-500",
    bg: "bg-teal-100",
    titleKey: "Track Your Progress",
    descKey: "Visual dashboards, weekly reports, activity calendar, and shareable profile cards. See how far you've come — body and mind.",
  },
];

const STORAGE_KEY = "lumifit_tour_completed";
const MAX_TOUR_VIEWS = 2;

export const WelcomeTour = ({ onComplete, forceShow = false, onForceClose }) => {
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (forceShow) {
      setStep(0);
      setVisible(true);
      return;
    }
    // Check server-side tour status
    axios.get(`${API}/tour/status`).then(res => {
      const views = res.data.tour_views || 0;
      if (views < MAX_TOUR_VIEWS) {
        setVisible(true);
      }
    }).catch(() => {
      // Fallback to localStorage if API fails
      const completed = localStorage.getItem(STORAGE_KEY);
      if (!completed) setVisible(true);
    });
  }, [forceShow]);

  const handleClose = () => {
    // Mark viewed on server
    axios.post(`${API}/tour/viewed`).catch(() => {});
    localStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
    setStep(0);
    if (onForceClose) onForceClose();
    if (onComplete) onComplete();
  };

  const handleNext = () => {
    if (step < TOUR_STEPS.length - 1) {
      setStep(step + 1);
    } else {
      handleClose();
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  if (!visible) return null;

  const current = TOUR_STEPS[step];
  const Icon = current.icon;
  const isLast = step === TOUR_STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      data-testid="welcome-tour-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-300"
        data-testid="welcome-tour-card"
      >
        {/* Header with skip */}
        <div className="flex justify-end p-3 pb-0">
          <button
            onClick={handleClose}
            className="text-slate-300 hover:text-slate-500 transition-colors p-1"
            data-testid="tour-skip-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-8 pb-2 text-center">
          <div className={`w-20 h-20 ${current.bg} rounded-2xl flex items-center justify-center mx-auto mb-5`}>
            <Icon className={`w-10 h-10 ${current.color}`} strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-light text-sky-900 mb-3" style={{ fontFamily: "Outfit" }}>
            {current.titleKey}
          </h2>
          <p className="text-slate-500 text-sm leading-relaxed">
            {current.descKey}
          </p>
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 py-5">
          {TOUR_STEPS.map((_, i) => (
            <button
              key={`dot-${i}`}
              onClick={() => setStep(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step ? "w-8 bg-sky-500" : "w-1.5 bg-slate-200 hover:bg-slate-300"
              }`}
              data-testid={`tour-dot-${i}`}
            />
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between px-6 pb-6">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={step === 0}
            className={`text-slate-400 ${step === 0 ? "invisible" : ""}`}
            data-testid="tour-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          <span className="text-xs text-slate-300">
            {step + 1} / {TOUR_STEPS.length}
          </span>

          <Button
            onClick={handleNext}
            className="glow-button"
            data-testid={isLast ? "tour-finish-btn" : "tour-next-btn"}
          >
            {isLast ? "Let's Go!" : "Next"}
            {!isLast && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Hook to reset tour (for Settings "Replay Tour" button)
export const resetWelcomeTour = () => {
  localStorage.removeItem(STORAGE_KEY);
  // Server-side reset not needed — forceShow bypasses the check
};
